/**
 * features.ts — per-candidate-leap features (the action representation).
 *
 * The agent picks among 32 candidate leaps (8 directions × 4 lengths) each
 * tick. To make that choice LEARNABLE we describe each candidate by reading
 * the belief map (the agent's spatial memory) — NOT the area around the
 * agent. This is the design fix that cracked the problem: the old GRU took
 * a single 87-dim observation of the agent's immediate neighbourhood and
 * had to infer which leap was best from that. Here, the NN's input IS the
 * candidate — one feature row per leap — and `argmax over Q(rows)` is the
 * choice. Learning "go where the believed-gold is" becomes a trivial
 * regression.
 *
 * Each row is N_FEATURES = 11 numbers:
 *   [0]  bGold(dest)            belief that destination cell has gold
 *   [1]  bStone(dest)           belief that destination is a stone
 *   [2]  bUnknown(dest)         destination is unexplored
 *   [3]  pathClear              1 if no believed stone blocks the leap, else 0
 *   [4]  goldDensityOnPath      Σ bGold on path / length   (density, not count)
 *   [5]  unkDensityOnPath       # unknown on path / length (density, not count)
 *   [6]  recentDensityOnPath    mean bRecent on path (high = just visited)
 *   [7]  stoneDensityOnPath     Σ bStone on path / length  (risk signal)
 *   [8]  raycastUnkAhead        # unknown cells along full ray / GRID
 *   [9]  raycastGoldAhead       Σ bGold along full ray / GRID
 *   [10] invLength              1/length  (lets the model learn a length prior)
 *
 * Why DENSITIES (not raw counts) on the path features: an earlier prototype
 * summed raw counts, which made Q grow monotonically with leap length (longer
 * leap = more cells crossed = more coverage) and the agent degenerated to
 * "always pick length-8" (379/400 actions were length-8, 358 bumps). Density
 * normalises by length so DIRECTION carries the signal, not length.
 *
 * Why a RAYCAST beyond the leap: a length-8 leap ends at a cell 8 away, but
 * the agent should know what lies even further in that direction. The raycast
 * sums bGold and unknown-count from the agent's pose to the grid edge along
 * the direction. This is the long-range pointer that breaks ties between
 * similarly-promising short leaps.
 */

import { Belief, UNKNOWN } from './belief';
import { DIRECTIONS, N_DIRS, LEAP_LENGTHS } from './environment';
import type { Env } from './environment';

export const N_ACTIONS = N_DIRS * 4; // 32 (4 leap lengths)
export const N_FEATURES = 11;

/** Walk up to `maxLen` cells from (r,c) in (dr,dc); return (unkCount, goldSum). */
function raycast(
  b: Belief,
  r: number,
  c: number,
  dr: number,
  dc: number,
  maxLen: number,
  grid: number,
): [number, number] {
  let unk = 0;
  let gold = 0;
  for (let k = 1; k <= maxLen; k++) {
    const nr = r + dr * k;
    const nc = c + dc * k;
    if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) break;
    const idx = nr * grid + nc;
    if (b.known[idx] === UNKNOWN) unk++;
    gold += b.bGold[idx];
  }
  return [unk, gold];
}

/**
 * Build the (N_ACTIONS × N_FEATURES) candidate-feature matrix for the
 * agent's current pose. The output buffer is row-major flat — row `a` starts
 * at offset `a * N_FEATURES`. Reuse a buffer across ticks to avoid GC.
 *
 * Pure function of (belief, r, c). No env mutation.
 */
export function candidateFeatures(env: Env, out: Float64Array): Float64Array {
  const b = env.belief;
  const r = env.r;
  const c = env.c;
  const grid = env.world.grid;
  for (let a = 0; a < N_ACTIONS; a++) {
    const dir = a % N_DIRS;
    const lenIdx = (a / N_DIRS) | 0;
    const length = LEAP_LENGTHS[lenIdx];
    const [dr, dc] = DIRECTIONS[dir];
    const destR = r + dr * length;
    const destC = c + dc * length;
    const off = a * N_FEATURES;

    // Destination features (cells beyond sensing range just read the prior).
    if (destR >= 0 && destR < grid && destC >= 0 && destC < grid) {
      const di = destR * grid + destC;
      out[off + 0] = b.bGold[di];
      out[off + 1] = b.bStone[di];
      out[off + 2] = b.known[di] === UNKNOWN ? 1 : 0;
    } else {
      // Out of bounds — make it unattractive by marking it as believed stone.
      out[off + 0] = 0;
      out[off + 1] = 1;
      out[off + 2] = 0;
    }

    // Path features (densities, not raw counts).
    let pathClear = 1;
    let goldSum = 0;
    let unkCount = 0;
    let recentSum = 0;
    let stoneSum = 0;
    for (let k = 1; k <= length; k++) {
      const nr = r + dr * k;
      const nc = c + dc * k;
      if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) {
        pathClear = 0;
        break;
      }
      const idx = nr * grid + nc;
      if (b.bStone[idx] >= 0.5) pathClear = 0;
      goldSum += b.bGold[idx];
      stoneSum += b.bStone[idx];
      if (b.known[idx] === UNKNOWN) unkCount++;
      recentSum += b.bRecent[idx];
    }
    const invLen = 1 / length;
    out[off + 3] = pathClear;
    out[off + 4] = goldSum * invLen;
    out[off + 5] = unkCount * invLen;
    out[off + 6] = recentSum * invLen;
    out[off + 7] = stoneSum * invLen;

    // Raycast to the grid edge along this direction.
    const [unkAhead, goldAhead] = raycast(b, r, c, dr, dc, grid - 1, grid);
    out[off + 8] = unkAhead / grid;
    out[off + 9] = goldAhead / grid;
    out[off + 10] = invLen;
  }
  return out;
}
